import React, { useState, useRef, useEffect } from 'react'
import { Field, Select, Icon } from '@arnsbogroup/react-voicearchive'
import { FormattedMessage, useIntl } from 'react-intl'
import useApi from 'hooks/useApi'
import messages from './messages'
import classNames from 'classnames'

const InlineMediaSelect = ({
  value = [],
  onChange = () => {},
  disabled = false
}) => {
  const [mediaOptions] = useApi(`/rest/v2/ordertargetmedia?search[hidden]=0&search[deleted]=0&pageSize=999`)
  const { formatMessage } = useIntl()
  const [isEditing, setIsEditing] = useState(false)
  const [internalValues, setInternalValues] = useState([])
  const containerRef = useRef(null)

  const allOptions = mediaOptions ? mediaOptions.map(({ uid, name }) => ({
    value: +uid,
    label: name
  })) : []

  const displayText = value.length
    ? value.map(m => `${m.name} (${m.spot_count})`).join(', ')
    : formatMessage(messages.mediaPlaceholder)

  const handleStartEdit = () => {
    if (disabled) {
      return
    }

    const initialValues = value.map(media => ({
      uid: media.uid,
      name: media.name,
      spot_count: media.spot_count || 1
    }))

    setInternalValues(initialValues)
    setIsEditing(true)
  }

  const handleSave = () => {
    const cleaned = internalValues.map(media => ({
      uid: media.uid,
      name: media.name,
      spot_count: !media.spot_count || media.spot_count === '' || Number(media.spot_count) < 1
        ? 1
        : Number(media.spot_count)
    }))

    onChange(cleaned)
    setIsEditing(false)
    setInternalValues([])
  }

  useEffect(() => {
    if (!isEditing) {
      return
    }

    const handleClickOutside = (event) => {
      const insideContainer = containerRef.current && containerRef.current.contains(event.target)
      // The Select's dropdown menu renders in a portal on document.body, so clicks
      // on its options land outside containerRef. Treat them as inside to avoid
      // saving/closing before the selection is applied.
      const insideMenu = event.target.closest && event.target.closest('.inline-media-select__menu')

      if (!insideContainer && !insideMenu) {
        handleSave()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isEditing, internalValues])

  const handleSelectMedia = (selectedOption) => {
    if (!selectedOption) {
      return
    }

    const exists = internalValues.find(m => m.uid === selectedOption.value)
    if (exists) {
      return
    }

    setInternalValues([
      ...internalValues,
      {
        uid: selectedOption.value,
        name: selectedOption.label,
        spot_count: 1
      }
    ])
  }

  const handleRemoveMedia = (uid) => {
    setInternalValues(internalValues.filter(m => m.uid !== uid))
  }

  const handleSpotCountChange = (uid, newValue) => {
    setInternalValues(prev =>
      prev.map(media =>
        media.uid === uid
          ? { ...media, spot_count: newValue === '' ? '' : Number(newValue) }
          : media
      )
    )
  }

  return (
    <Field label={formatMessage(messages.mediaPlaceholder)} >
      <div ref={containerRef}>
        {isEditing ? (
          <div className="border border-gray-300 rounded p-3 bg-white">
            <Select
              options={allOptions}
              onChange={handleSelectMedia}
              value={null}
              classNamePrefix="inline-media-select"
            />

            {internalValues.length > 0 ? (
              <div>
                {internalValues.map((media) => (
                  <div
                    key={media.uid}
                    className="flex items-start p-2 mb-1 bg-gray-50 rounded gap-2"
                  >
                    <span className="text-sm flex-1 my-auto">
                      {media.name}
                    </span>

                    <div className="flex flex-col items-end">
                      <div className="w-full text-xs text-left text-gray-400">
                        <span>
                          <FormattedMessage {...messages.spotCount} />
                        </span>
                      </div>
                  
                      <input
                        type="number"
                        min={1}
                        value={media.spot_count}
                        onChange={(e) => handleSpotCountChange(media.uid, e.target.value)}
                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded outline-none focus:border-primary"
                      />
                    </div>

                    <Icon
                      onClick={() => handleRemoveMedia(media.uid)}
                      className="text-negative cursor-pointer self-end pb-2"
                      icon={['fas', 'trash']}
                      title={formatMessage.delete}
                    />
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-4 text-center text-sm text-gray-400">
                <FormattedMessage {...messages.noMedia} />
              </div>
            )}
          </div>
        ) : (
          <span
            onClick={handleStartEdit}
            className={classNames({
              'border-b border-gray-300 hover:border-gray-700 border-dashed cursor-pointer': !disabled,
              'cursor-not-allowed': disabled,
              'text-gray-400': displayText == formatMessage(messages.mediaPlaceholder)
            })}
          >
            {displayText}
          </span>
        )}
      </div>
    </Field>
  )
}

export default InlineMediaSelect
